import { ReactNode } from 'react';

export enum SectionName {
  AccordionSection = 'AccordionSection',
  AnimationsSection = 'AnimationsSection',
  AppsSection = 'AppsSection',
  BlockGrid = 'BlockGrid',
  BlockSection = 'BlockSection',
  Card = 'Card',
  CarouselSection = 'CarouselSection',
  ColumnsGridSection = 'ColumnsGridSection',
  CompactCTASection = 'CompactCTASection',
  CTASection = 'CTASection',
  CtaStandardSection = 'CtaStandardSection',
  FakeSection = 'FakeSection',
  FAQSection = 'FAQSection',
  FeaturesSection = 'FeaturesSection',
  HeroFeatures = 'HeroFeatures',
  HeroSection = 'HeroSection',
  HeroStandard = 'HeroStandard',
  JobBoard = 'JobBoard',
  LogoBar = 'LogoBar',
  OneColumnBlock = 'OneColumnBlock',
  PlanComparisonSection = 'PlanComparisonSection',
  PopularTemplatesSection = 'PopularTemplatesSection',
  ResourcesSection = 'ResourcesSection',
  ScrollingTweetsSection = 'ScrollingTweetsSection',
  SectionWithSubNav = 'SectionWithSubNav',
  SignUpSection = 'SignUpSection',
  SplitColorAccordionSection = 'SplitColorAccordionSection',
  SplitColorHero = 'SplitColorHero',
  SubscribeSection = 'SubscribeSection',
  TableSection = 'TableSection',
  TabsSection = 'TabsSection',
  TestimonialCarousel = 'TestimonialCarousel',
  TestimonialMultipleColumn = 'TestimonialMultipleColumn',
  TestimonialSolo = 'TestimonialSolo',
  TestimonialsSection = 'TestimonialsSection',
  TwoColumnGridSection = 'TwoColumnGridSection',
  TwoColumnRiverSection = 'TwoColumnRiverSection',
  VideoSection = 'VideoSection',
  ZapCardSection = 'ZapCardSection',
}
export type SectionNameType = typeof SectionName[keyof typeof SectionName];

export enum CtaGoal {
  browseTemplates = 'browse_templates',
  getMoreInformation = 'get_more_information',
  exploreIntegrations = 'explore_integrations',
  upgrade = 'upgrade',
  fakeCtaGoal = 'fakeCtaGoal',
  signup = 'signup',
  webinarRegistration = 'webinarRegistration',
  eventRegistration = 'eventRegistration',
  scheduleCall = 'scheduleCall',
  createZap = 'createZap',
  transfer = 'transfer',
}
export type CtaGoalType = typeof CtaGoal[keyof typeof CtaGoal];

export enum CtaName {
  browseTemplates = 'browse_templates',
  exploreIntegrations = 'explore_integrations',
  features = 'features',
  tryThis = 'try_this',
  tabSelection = 'tab_selection',
  demo = 'demo',
  moreArticles = 'get_more_articles',
  readFullStory = 'read_full_story',
  betaOptIn = 'beta_opt_in',
  fakeCtaName = 'fakeCtaName',
  viewMore = 'viewMore',
  learnMore = 'learnMore',
  register = 'register',
  watchVideo = 'watchVideo',
  downloadContent = 'downloadContent',
  signup = 'signup',
  upgrade = 'upgrade',
  contactUs = 'contactUs',
  getStarted = 'getStarted',
}
export type CtaNameType = typeof CtaName[keyof typeof CtaName];

export enum EventName {
  NAVIGATE = 'navigate',
  PERFORMANCE_MOUNT = 'performance_mount',
  /**
   * We need PAGE_VIEW & NAVIAGETE_SSR for the time being so that we can
   * 1) track pages in the same way we have in the past and
   * 2) work towards moving to Avro events by end of 2021.
   */
  PAGE_VIEW = 'schema:web_analytics.tracking.page_view.PageViewEvent',
  SCROLLED_INTO_VIEW = 'component_scrolled_into_view',
  CALL_TO_ACTION = 'call_to_action',
  FAQ_ACCORDION_OPEN = 'faq_accordion_open',
  FAQ_ACCORDION_CLOSE = 'faq_accordion_close',
  FAQ_SECTION_CLOSE = 'faq_section_close',
  FAQ_SECTION_OPEN = 'faq_section_open',
  HUBSPOT_FORM_SUBMITTED = 'hubspot_form_submitted',
}

type NavigateEvent = Record<string, never>;
type PageViewEvent = {
  system_name: 'pagebuilder';
};
type PerformanceMountEvent = {
  timeToMount: number;
  timeToMountType: 'page-ready';
};

export type CallToActionEvent = {
  path: string;
  from_path: string;
  cta_name: CtaNameType;
  cta_raw_text: string | ReactNode;
  cta_goal: CtaGoalType;
  loging_type?: string;
  link_destination?: string;
  signup_type?: string;
  /**
   * This is coming from TrackingProvider's context
   */
  event_location?: SectionNameType;
};
type ScrolledIntoViewEvent = {
  component: SectionNameType;
};

type AccordionOpenEvent = {
  title: string;
};
type AccordionCloseEvent = {
  title: string;
};

type HubSpotFormSubmittedEvent = {
  path: string;
  title: string;
  hubspot_form_id: string;
  hubspot_portal_id: string;
};

type EventNamesAndData = {
  [EventName.NAVIGATE]: NavigateEvent;
  [EventName.PERFORMANCE_MOUNT]: PerformanceMountEvent;
  [EventName.PAGE_VIEW]: PageViewEvent;
  [EventName.SCROLLED_INTO_VIEW]: ScrolledIntoViewEvent;
  [EventName.CALL_TO_ACTION]: CallToActionEvent;
  [EventName.FAQ_ACCORDION_OPEN]: AccordionOpenEvent;
  [EventName.FAQ_ACCORDION_CLOSE]: AccordionCloseEvent;
  [EventName.FAQ_SECTION_OPEN]: AccordionOpenEvent;
  [EventName.FAQ_SECTION_CLOSE]: AccordionCloseEvent;
  [EventName.HUBSPOT_FORM_SUBMITTED]: HubSpotFormSubmittedEvent;
};

export type TrackEventFn = <EventNameArg extends EventName>(
  eventName: EventNameArg,
  data: EventNamesAndData[EventNameArg],
) => void;
